import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import {
  ChartBar, Users, Package, CurrencyDollar, BellRinging, Gear, Cloud, Megaphone,
  ArrowsLeftRight, DotsThreeOutline,
} from "@phosphor-icons/react";
import { useMemo } from "react";
import WhatsAppButton from "@/components/app/WhatsAppButton";
import { InstallPWAModal, InstallPWAButton } from "@/components/app/InstallPWA";
import { NotificationBell } from "@/components/app/NotificationBell";
import { Logo } from "@/components/app/Logo";
import { UserMenu } from "@/components/app/UserMenu";
import { Toaster } from "sonner";

// Full sidebar nav (desktop)
const SIDEBAR_NAV = [
  { to: "/admin", label: "Dashboard", icon: ChartBar, end: true },
  { to: "/admin/clientes", label: "Clientes", icon: Users },
  { to: "/admin/planos", label: "Planos", icon: Package },
  { to: "/admin/pagamentos", label: "Pagamentos", icon: CurrencyDollar },
  { to: "/admin/notificacoes", label: "Notificações", icon: BellRinging },
  { to: "/admin/campanhas", label: "Campanhas", icon: Megaphone },
  { to: "/admin/trocas", label: "Trocas de plano", icon: ArrowsLeftRight },
  { to: "/admin/configuracoes", label: "Configurações", icon: Gear },
  { to: "/admin/deploy", label: "Deploy grátis", icon: Cloud },
];

// Mobile bottom-nav groups (5 max for thumb comfort)
const MOBILE_GROUPS = [
  {
    id: "home",
    label: "Início",
    icon: ChartBar,
    main: "/admin",
    matches: ["/admin"],
    subtabs: [],
  },
  {
    id: "clientes",
    label: "Clientes",
    icon: Users,
    main: "/admin/clientes",
    matches: ["/admin/clientes", "/admin/planos"],
    subtabs: [
      { to: "/admin/clientes", label: "Clientes", icon: Users },
      { to: "/admin/planos", label: "Planos", icon: Package },
    ],
  },
  {
    id: "cobranca",
    label: "Cobrança",
    icon: CurrencyDollar,
    main: "/admin/pagamentos",
    matches: ["/admin/pagamentos"],
    subtabs: [],
  },
  {
    id: "avisos",
    label: "Avisos",
    icon: BellRinging,
    main: "/admin/notificacoes",
    matches: ["/admin/notificacoes", "/admin/campanhas"],
    subtabs: [
      { to: "/admin/notificacoes", label: "Notificações", icon: BellRinging },
      { to: "/admin/campanhas", label: "Campanhas", icon: Megaphone },
    ],
  },
  {
    id: "mais",
    label: "Mais",
    icon: DotsThreeOutline,
    main: "/admin/configuracoes",
    matches: ["/admin/configuracoes", "/admin/deploy", "/admin/trocas"],
    subtabs: [
      { to: "/admin/trocas", label: "Trocas de plano", icon: ArrowsLeftRight },
      { to: "/admin/configuracoes", label: "Configurações", icon: Gear },
      { to: "/admin/deploy", label: "Deploy grátis", icon: Cloud },
    ],
  },
];

const activeGroupFor = (pathname) => {
  // Prefer the most specific (longest) match
  let best = MOBILE_GROUPS[0];
  let bestLen = -1;
  for (const g of MOBILE_GROUPS) {
    for (const m of g.matches) {
      const isMatch = m === "/admin" ? pathname === "/admin" : pathname.startsWith(m);
      if (isMatch && m.length > bestLen) {
        best = g;
        bestLen = m.length;
      }
    }
  }
  return best;
};

const AdminLayout = () => {
  const { user } = useAuth();
  const nav = useNavigate();
  const location = useLocation();

  const activeGroup = useMemo(() => activeGroupFor(location.pathname), [location.pathname]);

  return (
    <div className="min-h-screen bg-zinc-50 text-zinc-900 lg:pb-0 pb-24">
      <Toaster position="top-right" />
      <InstallPWAModal openOnMount={true} />

      {/* Topbar mobile — respects device status bar / notch */}
      <header
        className="lg:hidden sticky top-0 z-30 bg-zinc-950 border-b border-zinc-900 pt-[env(safe-area-inset-top)]"
      >
        <div className="px-4 h-14 flex items-center justify-between">
          <Logo variant="full" height={28} />
          <div className="flex items-center gap-2 text-zinc-100">
            <NotificationBell scope="admin" />
            <UserMenu variant="dark" showSettings settingsPath="/admin/configuracoes" />
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar desktop */}
        <aside className="hidden lg:block w-64 bg-zinc-950 text-zinc-100 border-r border-zinc-900 min-h-screen sticky top-0">
          <div className="h-16 px-5 flex items-center border-b border-zinc-900">
            <Logo variant="full" height={36} />
          </div>
          <nav className="p-3 space-y-0.5">
            {SIDEBAR_NAV.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`admin-nav-${it.to.replace(/\//g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive ? "bg-white text-zinc-950" : "text-zinc-300 hover:bg-zinc-900"
                  }`
                }
              >
                <it.icon size={18} weight="regular" />
                {it.label}
              </NavLink>
            ))}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-zinc-900 bg-zinc-950 w-64">
            <div className="flex items-center gap-3">
              <UserMenu variant="dark" showSettings settingsPath="/admin/configuracoes" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate text-zinc-100">{user?.name}</div>
                <div className="text-[11px] tracking-wider uppercase text-zinc-500">Administrador</div>
              </div>
            </div>
          </div>
        </aside>

        {/* Main */}
        <main className="flex-1 min-h-screen min-w-0">
          <div className="hidden lg:flex items-center justify-end gap-2 px-8 pt-4">
            <InstallPWAButton />
            <NotificationBell scope="admin" />
          </div>

          {/* Mobile sub-tabs (pages-inside-pages) */}
          {activeGroup.subtabs.length > 1 && (
            <div className="lg:hidden sticky top-[calc(3.5rem+env(safe-area-inset-top))] z-20 bg-white/95 backdrop-blur border-b border-zinc-200 px-3 py-2">
              <div className="flex gap-1 overflow-x-auto no-scrollbar" data-testid="admin-subtabs">
                {activeGroup.subtabs.map((s) => (
                  <NavLink
                    key={s.to}
                    to={s.to}
                    end
                    data-testid={`admin-subtab-${s.to.replace(/\//g, "-")}`}
                    className={({ isActive }) =>
                      `flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                        isActive
                          ? "bg-zinc-900 text-white shadow-sm"
                          : "text-zinc-600 hover:bg-zinc-100"
                      }`
                    }
                  >
                    <s.icon size={14} weight="bold" />
                    {s.label}
                  </NavLink>
                ))}
              </div>
            </div>
          )}

          <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Mobile bottom nav (pill-active style) */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-zinc-950/95 backdrop-blur border-t border-zinc-800 px-2 pt-1.5 pb-[max(0.5rem,env(safe-area-inset-bottom))]">
        <div className="max-w-3xl mx-auto flex items-center justify-between gap-1">
          {MOBILE_GROUPS.map((g) => {
            const isActive = g.id === activeGroup.id;
            return (
              <button
                key={g.id}
                onClick={() => nav(g.main)}
                data-testid={`admin-bnav-${g.id}`}
                className={`flex items-center justify-center transition-all rounded-full overflow-hidden ${
                  isActive
                    ? "bg-blue-600 text-white px-3.5 py-2 flex-1 max-w-[140px] gap-1.5"
                    : "text-zinc-400 hover:text-zinc-100 w-11 h-11 flex-shrink-0"
                }`}
              >
                <g.icon
                  size={20}
                  weight={isActive ? "fill" : "regular"}
                  className="flex-shrink-0"
                />
                {isActive && (
                  <span className="text-xs font-semibold tracking-tight whitespace-nowrap">
                    {g.label}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      <WhatsAppButton />
    </div>
  );
};

export default AdminLayout;
